import { FC, memo, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { Counter } from "@/components/Counter";
import FlexContainer from "@/components/FlexContainer";
import { useInViewObserver } from "@/hooks/useInViewObserver";

import {
    StyledAddButton,
    StyledCounter,
    StyledPlusOutlined,
    StyledProductAdditionalInfoWrapper,
    StyledProductCardWithCounter,
    StyledProductCardWithCounterBottom,
    StyledProductCardWithCounterContainer,
    StyledProductCardWithCounterTop,
    StyledProductInfoText,
    StyledProductTitle,
} from "./ProductCardWithCounterUI.styled";

interface ProductCardWithCounterUIProps {
    name: string;
    barcode: string;
    additionalInfo?: React.ReactNode;

    counterType: "add" | "counter";
    counterValue?: number;
    onCounterChange?: (value: number) => void;
    counterMin?: number;
    counterMax?: number;

    isAddDisabled?: boolean;
    onAddTouchStart?: () => void;

    remaining?: string | number;
    onRemainingHandleTap?: () => void;
    remainingTitle?: string;

    isLast: boolean;
    onView: () => void;
}

export const ProductCardWithCounterUI: FC<ProductCardWithCounterUIProps> = memo(
    ({
        name,
        barcode,
        additionalInfo,
        counterType,
        counterValue,
        onCounterChange,
        counterMin = 0,
        counterMax,
        isAddDisabled = false,
        onAddTouchStart,
        remaining,
        onRemainingHandleTap,
        isLast,
        onView,
        remainingTitle = "Залишок:",
    }) => {
        const navigate = useNavigate();
        const cardRef = useRef<HTMLDivElement>(null);

        const handleNavigate = useCallback(
            () => navigate(`/work-plan/product-card/${barcode}`),
            [navigate, barcode],
        );

        const handleAddTouchStart = useCallback(() => {
            setTimeout(() => onAddTouchStart?.(), 100);
        }, [onAddTouchStart]);

        const counterKey = useMemo(
            () => `${barcode}-${String(counterMax ?? "")}`,
            [barcode, counterMax],
        );

        const counterNode = useMemo(() => {
            if (counterType === "add") {
                return (
                    <StyledAddButton
                        $isDisabled={isAddDisabled}
                        icon={
                            <StyledPlusOutlined
                                $isCountDisabled={isAddDisabled}
                            />
                        }
                        onTouchStart={handleAddTouchStart}
                    />
                );
            }

            return (
                <Counter
                    key={counterKey}
                    value={counterValue ?? 0}
                    onChange={onCounterChange ?? (() => {})}
                    min={counterMin}
                    max={counterMax}
                />
            );
        }, [
            counterType,
            isAddDisabled,
            handleAddTouchStart,
            counterKey,
            counterValue,
            onCounterChange,
            counterMin,
            counterMax,
        ]);

        const isRemainingVisible = useMemo(
            () => remaining === 0 || !!remaining,
            [remaining],
        );

        const handleInView = useCallback(() => {
            if (isLast) onView();
        }, [isLast, onView]);

        useInViewObserver(cardRef, handleInView);

        return (
            <StyledProductCardWithCounter ref={cardRef}>
                <StyledProductCardWithCounterContainer>
                    <StyledProductCardWithCounterTop>
                        <StyledProductTitle onClick={handleNavigate}>
                            {name}
                        </StyledProductTitle>
                        {counterNode}
                    </StyledProductCardWithCounterTop>

                    <StyledProductCardWithCounterBottom>
                        <StyledProductAdditionalInfoWrapper>
                            <StyledProductInfoText>
                                Штрихкод: {barcode}
                            </StyledProductInfoText>

                            {isRemainingVisible && (
                                <FlexContainer gap="5px">
                                    <StyledProductInfoText
                                        onTouchStart={onRemainingHandleTap}
                                        style={{ touchAction: "manipulation" }}
                                    >
                                        {remainingTitle}{" "}
                                    </StyledProductInfoText>
                                    <StyledCounter isPositive={remaining !== 0}>
                                        {remaining}
                                    </StyledCounter>
                                </FlexContainer>
                            )}
                        </StyledProductAdditionalInfoWrapper>

                        {additionalInfo}
                    </StyledProductCardWithCounterBottom>
                </StyledProductCardWithCounterContainer>
            </StyledProductCardWithCounter>
        );
    },
);

ProductCardWithCounterUI.displayName = "ProductCardWithCounterUI";
